'use client'

import React, { useState } from 'react'
import Select from 'react-select'
import type { StepperBlock } from '@/payload-types'
import { PillCap } from '@/components/ui/PillCap'

type StepperStep = NonNullable<StepperBlock['steps']>[number]
type InlineFormField = NonNullable<StepperStep['fields']>[number]
type FormFieldValue = string | string[] | boolean

interface InlineFormRendererProps {
  fields: InlineFormField[]
  className?: string
  onValidate?: (data: Record<string, FormFieldValue>) => void
  onSubmitSuccess?: (data: Record<string, FormFieldValue>) => void
  isLastStep?: boolean
  overrideColor?: string
  hideButtons?: boolean
  onBack?: () => void
  showBack?: boolean
}

export const InlineFormRenderer: React.FC<InlineFormRendererProps> = ({
  fields,
  className = '',
  onValidate,
  onSubmitSuccess,
  isLastStep = false,
  overrideColor = 'primary',
  hideButtons = false,
  onBack,
  showBack = false,
}) => {
  const [formData, setFormData] = useState<Record<string, FormFieldValue>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  if (!fields || fields.length === 0) {
    return <div className="alert alert-warning">Aucun champ défini pour cette étape.</div>
  }

  const colorClass = `color${overrideColor.toLowerCase()}`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // If this is NOT the last step, just validate and move to next
      if (onValidate && !isLastStep) {
        onValidate(formData)
        setFormData({})
        setIsSubmitting(false)
        return
      }

      // If this IS the last step, submit all data
      if (onSubmitSuccess && isLastStep) {
        onSubmitSuccess(formData)
        setIsSubmitting(false)
        return
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Une erreur est survenue')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFieldChange = (fieldName: string, value: FormFieldValue) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  return (
    <form onSubmit={handleSubmit} className={`payload-form ${colorClass} ${className}`}>
      {fields.map((field) => {
        const fieldName = field.name || field.label?.toLowerCase().replace(/\s+/g, '-') || ''

        switch (field.blockType) {
          case 'text':
          case 'email':
            return (
              <fieldset key={field.id} className="inputField">
                <input
                  placeholder=""
                  type={field.blockType === 'email' ? 'email' : 'text'}
                  id={fieldName}
                  name={fieldName}
                  required={field.required ?? undefined}
                  value={(formData[fieldName] as string) || ''}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                />
                <label htmlFor={fieldName}>
                  {field.label}
                  {field.required && <span>*</span>}
                </label>
              </fieldset>
            )

          case 'textarea':
            return (
              <fieldset key={field.id} className="withText">
                <textarea
                  id={fieldName}
                  placeholder=""
                  name={fieldName}
                  required={field.required ?? undefined}
                  rows={4}
                  value={(formData[fieldName] as string) || ''}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                />
                <label htmlFor={fieldName}>
                  {field.label}
                  {field.required && <span>*</span>}
                </label>
              </fieldset>
            )

          case 'select':
            const selectOptions =
              field.options?.map((option) => ({
                value: option.value,
                label: option.label,
              })) || []

            const isMultiple = field.allowMultiple === true

            // For multiple select
            if (isMultiple) {
              const selectedMultiOptions = Array.isArray(formData[fieldName])
                ? selectOptions.filter((opt) => (formData[fieldName] as string[]).includes(opt.value))
                : []

              return (
                <fieldset key={field.id}>
                  <label htmlFor={fieldName}>
                    {field.label}
                    {field.required && <span>*</span>}
                  </label>
                  <Select
                    instanceId={fieldName}
                    inputId={fieldName}
                    name={fieldName}
                    options={selectOptions}
                    value={selectedMultiOptions}
                    onChange={(selected) => {
                      const values = selected ? selected.map((s) => s.value) : []
                      handleFieldChange(fieldName, values)
                    }}
                    placeholder="Sélectionner..."
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    classNames={{
                      menuPortal: () => colorClass,
                    }}
                  />
                  {field.required && (
                    <input
                      type="text"
                      value={
                        Array.isArray(formData[fieldName]) && formData[fieldName].length > 0
                          ? 'valid'
                          : ''
                      }
                      required
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={() => {}}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    />
                  )}
                </fieldset>
              )
            }

            // For single select
            const selectedOption =
              selectOptions.find((opt) => opt.value === formData[fieldName]) || null

            return (
              <fieldset key={field.id}>
                <label htmlFor={fieldName}>
                  {field.label}
                  {field.required && <span>*</span>}
                </label>
                <Select
                  instanceId={fieldName}
                  inputId={fieldName}
                  name={fieldName}
                  options={selectOptions}
                  value={selectedOption}
                  onChange={(selected) => handleFieldChange(fieldName, selected?.value || '')}
                  placeholder="Sélectionner..."
                  isClearable={!field.required}
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  classNames={{
                    menuPortal: () => colorClass,
                  }}
                />
                {field.required && (
                  <input
                    type="text"
                    value={(formData[fieldName] as string) || ''}
                    required
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={() => {}}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                  />
                )}
              </fieldset>
            )

          case 'checkbox':
            return (
              <fieldset key={field.id}>
                <div className="inputCheckBox">
                  <input
                    type="checkbox"
                    id={fieldName}
                    name={fieldName}
                    required={field.required ?? undefined}
                    checked={(formData[fieldName] as boolean) || false}
                    onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
                  />
                  <label htmlFor={fieldName}>
                    {field.label}
                    {field.required && <span>*</span>}
                  </label>
                </div>
              </fieldset>
            )

          case 'number':
            return (
              <fieldset key={field.id} className="inputField">
                <input
                  type="number"
                  id={fieldName}
                  placeholder=""
                  name={fieldName}
                  required={field.required ?? undefined}
                  value={(formData[fieldName] as string) || ''}
                  onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                />
                <label htmlFor={fieldName}>
                  {field.label}
                  {field.required && <span>*</span>}
                </label>
              </fieldset>
            )

          default:
            return null
        }
      })}

      {submitError && <div className="alert alert-danger">{submitError}</div>}

      {!hideButtons && (
        <div className="formFooter">
          {showBack && onBack && (
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              Précédent
            </button>
          )}
          <button className="btn btnSubmit btn-pill" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Traitement...' : isLastStep ? 'Envoyer' : 'Suivant'}
            <PillCap />
          </button>
        </div>
      )}
    </form>
  )
}
